/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {combineEpics, Epic} from 'redux-observable';
import {filter, ignoreElements, map, mapTo, mergeMap, tap} from 'rxjs/operators';
import {accountRecoveryResource, iamStompClient} from '../services';
import {ACCOUNT_RECOVERY_STATE_TOPIC_PREFIX} from '../services/Stomp/topics';
import {isActionOf} from 'typesafe-actions';
import {
	accountRecoverySceneActivated,
	accountRecoverySceneBackButtonClicked,
	accountRecoverySceneDeactivated,
	accountRecoverySceneEnterPasswordUnexpectedError,
	accountRecoverySceneEnterPhoneVerificationCodeUnexpectedError,
	accountRecoveryScenePasswordInputChanged,
	accountRecoveryScenePasswordStrengthEvaluated,
	accountRecoveryScenePasswordSubmitted,
	accountRecoveryScenePhoneVerificationCodeSubmitted,
	accountRecoverySceneRequestRestartPhoneVerification,
	accountRecoverySceneRequestRetryPhoneVerification,
	accountRecoverySceneRestartPhoneVerificationUnexpectedError,
	accountRecoverySceneRetryPhoneVerificationUnexpectedError,
	accountRecoverySceneStateChanged,
	accountRecoverySceneStateRetrieved
} from '../scenes/AccountRecovery/actions';
import {stompClientSubscribedToTopic, stompClientUnsubscribedFromTopic, stompMessageReceived} from '../services/Stomp/actions';
import {Route as LoginUIRoute} from '../routes';
import {STATE_CHANGE_FOR_ACCOUNT_RECOVERY} from '../services/Stomp/EventType';
import {violationsWithErrorMessages} from '../model/validation';
import {
	iamApiAccountRecoveryEnterPasswordUnexpectedError,
	iamApiAccountRecoveryEnterPasswordValidationException,
	iamApiAccountRecoveryEnterPhoneVerificationCodeUnexpectedError,
	iamApiAccountRecoveryEnterPhoneVerificationCodeValidationException,
	iamApiAccountRecoveryPasswordStrengthEvaluated,
	iamApiAccountRecoveryRestartPhoneVerificationUnexpectedError,
	iamApiAccountRecoveryRetryPhoneVerificationUnexpectedError,
	iamApiAccountRecoveryStateRetrieved
} from '../services/IamApi/actions';
import {AccountRecoveryStateDTO} from '../generated/iam-client';
import {resetPasswordSceneStartAccountRecoveryValidationException} from '../scenes/ResetPassword/actions';
import {navigate} from '../components/OutHook';

const accountRecoveryIdFromQueryString: () => string =
	() => new URLSearchParams(window.document.location.search).get('accountRecoveryId') || '';

const subscribeToAccountRecoveryStateTopicOnSceneActivate: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoverySceneActivated)),
	mergeMap(() => iamStompClient.subscribe(ACCOUNT_RECOVERY_STATE_TOPIC_PREFIX + accountRecoveryIdFromQueryString()))
);

// noinspection DuplicatedCode
const unsubscribeFromAccountRecoveryStateTopicOnSceneDeactivate: Epic = (action$) => {
	let subscribedTopic: string | null = null;

	action$.pipe(
		filter(isActionOf(stompClientSubscribedToTopic)),
		filter((action) => action.payload.topic.startsWith(ACCOUNT_RECOVERY_STATE_TOPIC_PREFIX))
	).subscribe((action) => subscribedTopic = action.payload.topic);

	action$.pipe(
		filter(isActionOf(stompClientUnsubscribedFromTopic)),
		filter((action) => action.payload.topic === subscribedTopic)
	).subscribe(() => subscribedTopic = null);

	return action$.pipe(
		filter(isActionOf(accountRecoverySceneDeactivated)),
		filter(() => subscribedTopic !== null),
		mergeMap(() => iamStompClient.unsubscribe(String(subscribedTopic)))
	);
};

const retrieveAccountRecoveryStateOnSceneActivate: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoverySceneActivated)),
	mergeMap(() => accountRecoveryResource.accountRecoveryState(accountRecoveryIdFromQueryString()))
);

const notifySceneOfRetrievedState: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryStateRetrieved)),
	map((action) => accountRecoverySceneStateRetrieved(action.payload))
);

const notifySceneOfStateChange: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === STATE_CHANGE_FOR_ACCOUNT_RECOVERY),
	map((action) => accountRecoverySceneStateChanged({state: action.payload.eventBody as unknown as AccountRecoveryStateDTO}))
);

const retryPhoneVerificationOnRequest: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoverySceneRequestRetryPhoneVerification)),
	mergeMap(() => accountRecoveryResource.retryPhoneVerification(accountRecoveryIdFromQueryString()))
);

const notifySceneOfPhoneVerificationRetryUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryRetryPhoneVerificationUnexpectedError)),
	mapTo(accountRecoverySceneRetryPhoneVerificationUnexpectedError())
);

const restartPhoneVerificationOnRequest: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoverySceneRequestRestartPhoneVerification)),
	mergeMap(() => accountRecoveryResource.restartPhoneVerification(accountRecoveryIdFromQueryString()))
);

const notifySceneOfPhoneVerificationRestartUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryRestartPhoneVerificationUnexpectedError)),
	mapTo(accountRecoverySceneRestartPhoneVerificationUnexpectedError())
);

const enterPhoneVerificationCodeOnSubmit: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoveryScenePhoneVerificationCodeSubmitted)),
	mergeMap((action) => accountRecoveryResource.enterPhoneVerificationCode(accountRecoveryIdFromQueryString(), action.payload.command))
);

const notifySceneOfEnterPhoneVerificationCodeValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryEnterPhoneVerificationCodeValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return resetPasswordSceneStartAccountRecoveryValidationException({
			violations: {
				verificationCode: violations.verificationCode || []
			}
		});
	})
);

const notifySceneOfEnterPhoneVerificationCodeUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryEnterPhoneVerificationCodeUnexpectedError)),
	mapTo(accountRecoverySceneEnterPhoneVerificationCodeUnexpectedError())
);

const evaluatePasswordStrengthOnChange: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoveryScenePasswordInputChanged)),
	mergeMap((action) => accountRecoveryResource.evaluatedPasswordStrength(
		accountRecoveryIdFromQueryString(), action.payload.evaluationRequest
	))
);

const notifySceneOfEvaluatedPasswordStrength: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryPasswordStrengthEvaluated)),
	map((action) => accountRecoveryScenePasswordStrengthEvaluated(action.payload))
);

const enterPasswordOnSubmit: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoveryScenePasswordSubmitted)),
	mergeMap((action) => accountRecoveryResource.enterPassword(accountRecoveryIdFromQueryString(), action.payload.command))
);

const notifySceneOfPasswordValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryEnterPasswordValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return resetPasswordSceneStartAccountRecoveryValidationException({
			violations: {
				password: violations.password || []
			}
		});
	})
);

const notifySceneOfEnterPasswordUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountRecoveryEnterPasswordUnexpectedError)),
	mapTo(accountRecoverySceneEnterPasswordUnexpectedError())
);

const navigateToLoginOnClick: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountRecoverySceneBackButtonClicked)),
	tap(() => navigate(LoginUIRoute.LOGIN)),
	ignoreElements()
);

const accountRecoveryEpics: Epic = combineEpics(
	subscribeToAccountRecoveryStateTopicOnSceneActivate,
	unsubscribeFromAccountRecoveryStateTopicOnSceneDeactivate,
	retrieveAccountRecoveryStateOnSceneActivate,
	notifySceneOfRetrievedState,
	notifySceneOfStateChange,
	retryPhoneVerificationOnRequest,
	notifySceneOfPhoneVerificationRetryUnexpectedError,
	restartPhoneVerificationOnRequest,
	notifySceneOfPhoneVerificationRestartUnexpectedError,
	enterPhoneVerificationCodeOnSubmit,
	notifySceneOfEnterPhoneVerificationCodeValidationError,
	notifySceneOfEnterPhoneVerificationCodeUnexpectedError,
	evaluatePasswordStrengthOnChange,
	notifySceneOfEvaluatedPasswordStrength,
	enterPasswordOnSubmit,
	notifySceneOfPasswordValidationError,
	notifySceneOfEnterPasswordUnexpectedError,
	navigateToLoginOnClick
);

export default accountRecoveryEpics;
